import type { VehicleTypeOption } from './api'

const FALLBACK_VEHICLE_TYPES: VehicleTypeOption[] = [
  { code: '1.5', label: '1.5 тонны', short_label: '1,5 т.' },
  { code: '3', label: '3 тонны', short_label: '3 т.' },
  { code: '5', label: '5 тонн', short_label: '5 т.' },
  { code: '10', label: '10 тонн', short_label: '10 т.' },
  { code: '20', label: '20 тонн', short_label: '20 т.' },
  { code: '20_coupling', label: 'Сцепка 20 тонн', short_label: 'Сцепка 20 т.' },
  { code: 'special', label: 'Спецтранспорт', short_label: 'Спец.' },
]

// Process-wide cache of the transport catalog. The backend (GET /api/catalog/vehicle-types)
// is the single source of truth for codes + labels; this module just holds the fetched copy
// so synchronous display helpers (translateVehicleType) and form dropdowns can read it
// without each carrying their own hard-coded list. Populated once via useVehicleTypes.
let options: VehicleTypeOption[] = [...FALLBACK_VEHICLE_TYPES]
let labelByCode: Record<string, string> = Object.fromEntries(
  FALLBACK_VEHICLE_TYPES.map((option) => [option.code, option.label]),
)
let shortLabelByCode: Record<string, string> = Object.fromEntries(
  FALLBACK_VEHICLE_TYPES.map((option) => [option.code, option.short_label || option.label]),
)

// Whether the server's catalog has arrived. The fallback above fills `options` from the start, so
// its length cannot tell — and treating it as loaded meant the catalog was never fetched at all.
let loadedFromServer = false

export function vehicleTypesLoaded(): boolean {
  return loadedFromServer
}

export function setVehicleTypes(list: VehicleTypeOption[]): void {
  if (list.length > 0) loadedFromServer = true
  const nextList = list.length > 0 ? list : FALLBACK_VEHICLE_TYPES
  options = [...nextList]
  labelByCode = Object.fromEntries(nextList.map((option) => [option.code, option.label]))
  shortLabelByCode = Object.fromEntries(nextList.map((option) => [option.code, option.short_label || option.label]))
}

export function getVehicleTypeOptions(): VehicleTypeOption[] {
  return options
}

// The stored type if it is a catalog code, otherwise ''. The edit form starts from this: a value
// the dropdown can't show would look like «not chosen» on screen yet still be sent — and the
// server refuses anything outside the catalog.
export function knownVehicleType(code?: string | null): string {
  return code && Object.prototype.hasOwnProperty.call(labelByCode, code) ? code : ''
}

// Russian label for a transport code, falling back to the code itself when the catalog
// hasn't loaded yet or the code is unknown.
export function vehicleTypeLabel(code?: string | null): string {
  if (!code) return '—'
  return labelByCode[code] || code
}

/** Compact form for route-card chips, e.g. «20 т.» instead of «20 тонн». */
export function vehicleTypeShortLabel(code?: string | null): string {
  if (!code) return '—'
  return shortLabelByCode[code] || labelByCode[code] || code
}
